import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import ExpandMore from '@material-ui/icons/ExpandMoreOutlined';
import ErrorIcon from '@material-ui/icons/ErrorOutlined';
import useIsMobile from '../../../hooks/useIsMobile';

const useStyles = orientation => makeStyles(theme => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        display: orientation === 'vertical' ? 'flex' : 'initial',
        minWidth: 0
    },
    appBar: {
        backgroundColor: theme.palette.primary.light,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4
    },
    tabPanel: {
        width: '100%',
        minWidth: 0,
        padding: theme.spacing(2)
    },
    errorLabelContainer: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.error.main
    },
    errorAccordion: {
        borderColor: theme.palette.error.main
    },
    errorSummary: {
        borderLeftColor: theme.palette.error.main,
        color: theme.palette.error.main,
        '& $mobileSummaryLabel': {
            color: theme.palette.error.main
        }
    },
    errorIcon: {
        color: theme.palette.error.main,
        marginLeft: theme.spacing(1)
    },
    mobileRoot: {
        width: '100%'
    },
    mobileAccordion: {
        '&:before': {
            display: 'none'
        },
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '&:first-child': {
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius
        },
        '&:last-child': {
            borderBottomLeftRadius: theme.shape.borderRadius,
            borderBottomRightRadius: theme.shape.borderRadius
        }
    },
    mobileSummary: {
        minHeight: 52,
        padding: theme.spacing(0, 1.5),
        backgroundColor: theme.palette.background.paper,
        borderLeft: `4px solid ${theme.palette.primary.light}`,
        color: theme.palette.primary.main,
        transition: theme.transitions.create(['background-color', 'color']),
        '& .MuiAccordionSummary-expandIcon': {
            color: theme.palette.primary.main
        },
        '&.Mui-expanded': {
            minHeight: 52,
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main,
            '& .MuiAccordionSummary-expandIcon': {
                color: theme.palette.primary.main
            },
            '& $mobileSummaryLabel': {
                color: theme.palette.primary.main
            },
            '& $mobileSummaryDescription': {
                color: theme.palette.secondary.dark
            }
        },
        '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            margin: theme.spacing(1, 0),
            '&.Mui-expanded': {
                margin: theme.spacing(1, 0)
            }
        }
    },
    mobileSummaryLabel: {
        flex: 1,
        minWidth: 0,
        color: theme.palette.primary.main,
        fontWeight: 700,
        overflowWrap: 'anywhere'
    },
    mobileSummaryDescription: {
        display: 'block',
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        fontWeight: 400,
        marginTop: theme.spacing(.25)
    },
    mobileDetails: {
        display: 'block',
        padding: 0,
        minWidth: 0
    },
    mobileContent: {
        minWidth: 0,
        padding: theme.spacing(0, 2, 2)
    },
    mobileError: {
        color: theme.palette.error.main,
        marginLeft: theme.spacing(1),
        flexShrink: 0
    }
}));

function TabPanel(props) {
    const {
        children,
        value,
        index,
        label,
        content,
        hasError,
        visible,
        summary,
        disabled,
        id,
        ...other
    } = props;
    const classes = useStyles()();

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${id || index}`}
            aria-labelledby={`tab-${id || index}`}
            className={classes.tabPanel}
            {...other}>
            {value === index && children}
        </div>
    );
}

function a11yProps(id, index) {
    const suffix = id || index;
    return {
        id: `tab-${suffix}`,
        'aria-controls': `tabpanel-${suffix}`
    };
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
};

const ConditionalAppBar = ({noAppBar, children}) => {
    const classes = useStyles()();

    return !noAppBar
        ? <AppBar position="static" className={classes.appBar}>{children}</AppBar>
        : children;
};

const GenericTab = forwardRef((props, ref) => {
    const {
        index = 0,
        content = [],
        noAppBar = false,
        orientation = 'horizontal',
        mobileVariant = 'none',
        activeId,
        onChange,
        ...rest
    } = props;
    const visibleContent = content.filter(item => item && item.visible !== false);
    const classes = useStyles(orientation)();
    const isMobile = useIsMobile();
    const [tabIndex, setTabIndex] = useState(() => Math.min(
        Math.max(index, 0),
        Math.max(visibleContent.length - 1, 0)
    ));

    useEffect(() => {
        if (!visibleContent.length) {
            return;
        }

        setTabIndex(previous => Math.min(previous, visibleContent.length - 1));
    }, [visibleContent.length]);

    useEffect(() => {
        if (onChange && visibleContent[tabIndex]) {
            onChange(tabIndex, visibleContent[tabIndex]);
        }
    }, [onChange, tabIndex, visibleContent.length]);

    useImperativeHandle(ref, () => ({
        resetTabIndex: () => setTabIndex(0),
        setTabIndex: value => setTabIndex(previous => {
            const nextValue = typeof value === 'function' ? value(previous) : value;
            return Math.min(Math.max(Number(nextValue) || 0, 0), Math.max(visibleContent.length - 1, 0));
        }),
        tabIndex
    }), [tabIndex, visibleContent.length]);

    const onTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const renderLabel = item => item.hasError ? (
        <div className={classes.errorLabelContainer}>
            {item.label}
            <ErrorIcon className={classes.errorIcon} fontSize="small" />
        </div>
    ) : item.label;

    const renderMobile = () => (
        <div className={classes.mobileRoot}>
            {visibleContent.map((item, itemIndex) => {
                const itemId = item.id || itemIndex;
                const expanded = tabIndex === itemIndex;

                return (
                    <Accordion
                        key={itemId}
                        expanded={expanded}
                        disabled={item.disabled}
                        className={clsx(classes.mobileAccordion, item.hasError && classes.errorAccordion)}
                        onChange={() => !item.disabled && setTabIndex(itemIndex)}>
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            className={clsx(classes.mobileSummary, item.hasError && classes.errorSummary)}
                            id={`tab-${itemId}`}
                            aria-controls={`tabpanel-${itemId}`}
                            aria-label={typeof item.label === 'string' ? item.label : undefined}>
                            <div className={classes.mobileSummaryLabel}>
                                <Typography component="span">
                                    {item.label}
                                </Typography>
                                {item.summary && (
                                    <span className={classes.mobileSummaryDescription}>{item.summary}</span>
                                )}
                            </div>
                            {item.hasError && <ErrorIcon className={classes.mobileError} fontSize="small" aria-label="Seção com erro" />}
                        </AccordionSummary>
                        {expanded && (
                            <AccordionDetails className={classes.mobileDetails}>
                                <div className={classes.mobileContent}>
                                    {item.content}
                                </div>
                            </AccordionDetails>
                        )}
                    </Accordion>
                );
            })}
        </div>
    );

    /* Modo seção única: exibe apenas o conteúdo pedido, sem barra de abas nem accordion. */
    if (activeId) {
        const activeItem = visibleContent.find(item => item.id === activeId) || visibleContent[0];

        return activeItem ? (
            <div
                id={`tabpanel-${activeItem.id}`}
                className={classes.mobileRoot}>
                {activeItem.content}
            </div>
        ) : null;
    }

    if (isMobile && mobileVariant === 'accordion') {
        return renderMobile();
    }

    return (
        <div className={classes.root}>
            {!!visibleContent.length && (
                <ConditionalAppBar noAppBar={noAppBar}>
                    <Tabs
                        value={tabIndex}
                        onChange={onTabChange}
                        variant="scrollable"
                        scrollButtons="on"
                        aria-label="Tabela de dados"
                        orientation={orientation}
                        {...rest}>
                        {visibleContent.map((item, itemIndex) => (
                            <Tab
                                key={item.id || itemIndex}
                                label={renderLabel(item)}
                                {...a11yProps(item.id, itemIndex)}
                                disabled={item.disabled}
                            />
                        ))}
                    </Tabs>
                </ConditionalAppBar>
            )}
            {!!visibleContent.length && visibleContent.map((item, itemIndex) => (
                <TabPanel
                    key={item.id || itemIndex}
                    value={tabIndex}
                    index={itemIndex}
                    {...item}>
                    {item.content}
                </TabPanel>
            ))}
        </div>
    );
});

GenericTab.propTypes = {
    index: PropTypes.number,
    content: PropTypes.array.isRequired,
    noAppBar: PropTypes.bool,
    mobileVariant: PropTypes.oneOf(['none', 'accordion']),
    activeId: PropTypes.string,
    onChange: PropTypes.func
};

export default GenericTab;
